/*
 * Consent agreement modal.
 *
 * The signature is captured as stroke coordinates and exported as an SVG path rather
 * than a PNG. A path is normally 1 to 3KB of plain text, small enough to live in a
 * line item property, which means the existing JSON post to /cart/add.js does not have
 * to become a multipart upload. It also scales to any size without going fuzzy when
 * you render it back later.
 *
 * Public API, used by the add to cart handlers in custom.js:
 *   vmConsent.isPresent()            a consent form exists on this page
 *   vmConsent.isSigned()             it has been completed
 *   vmConsent.mergeProperties(obj)   returns obj plus the consent properties
 */
(function () {
  'use strict';

  if (window.vmConsent) { return; }

  var PATH_LIMIT = 6000; // characters, well inside the line item property ceiling
  var signed = false;
  var collected = null;
  var pad = null;

  function root() {
    /* Quick add can be opened from a product page, which already has its own
       agreement in the DOM. The injected one wins while the drawer is open,
       otherwise the buyer would be signing the wrong product's agreement. */
    return (
      document.querySelector('[data-vm-consent][data-quick-add]') ||
      document.querySelector('[data-vm-consent]')
    );
  }

  /* ---------------------------------------------------------------- signature */

  function createPad(canvas) {
    var ctx = canvas.getContext('2d');
    var strokes = [];
    var current = null;

    function style() {
      var ratio = window.devicePixelRatio || 1;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = getComputedStyle(canvas).color || '#111';
    }

    function redraw() {
      var rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      strokes.forEach(function (stroke) {
        if (stroke.length < 2) { return; }
        ctx.beginPath();
        ctx.moveTo(stroke[0][0], stroke[0][1]);
        for (var i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i][0], stroke[i][1]);
        }
        ctx.stroke();
      });
    }

    function resize() {
      var rect = canvas.getBoundingClientRect();
      if (!rect.width) { return; }
      var ratio = window.devicePixelRatio || 1;
      // Setting width or height wipes the context, so restyle and repaint after.
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      style();
      redraw();
    }

    function point(event) {
      var rect = canvas.getBoundingClientRect();
      return [event.clientX - rect.left, event.clientY - rect.top];
    }

    canvas.addEventListener('pointerdown', function (event) {
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      current = [point(event)];
      strokes.push(current);
    });

    canvas.addEventListener('pointermove', function (event) {
      if (!current) { return; }
      event.preventDefault();
      var p = point(event);
      var last = current[current.length - 1];
      // Skip sub-pixel jitter: fewer points means a smaller path.
      if (Math.abs(p[0] - last[0]) < 1 && Math.abs(p[1] - last[1]) < 1) { return; }
      current.push(p);
      ctx.beginPath();
      ctx.moveTo(last[0], last[1]);
      ctx.lineTo(p[0], p[1]);
      ctx.stroke();
    });

    function end(event) {
      if (!current) { return; }
      if (event && event.pointerId != null && canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
      current = null;
    }

    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('pointerleave', end);

    window.addEventListener('resize', resize);

    return {
      resize: resize,
      isEmpty: function () {
        return !strokes.some(function (s) { return s.length > 1; });
      },
      clear: function () {
        strokes = [];
        current = null;
        redraw();
      },
      toPath: function () {
        var rect = canvas.getBoundingClientRect();
        var use = strokes.filter(function (s) { return s.length > 1; });

        function build(list) {
          return list
            .map(function (stroke) {
              return 'M' + stroke
                .map(function (p) { return round(p[0]) + ' ' + round(p[1]); })
                .join('L');
            })
            .join('');
        }

        var d = build(use);

        // Thin the points out rather than truncating, so a long signature degrades
        // into a slightly coarser one instead of a cut-off one.
        var guard = 0;
        while (d.length > PATH_LIMIT && guard < 8) {
          use = use.map(function (stroke) {
            if (stroke.length < 4) { return stroke; }
            var thinned = stroke.filter(function (_, i) { return i % 2 === 0; });
            thinned.push(stroke[stroke.length - 1]);
            return thinned;
          });
          d = build(use);
          guard++;
        }

        return Math.round(rect.width) + ' ' + Math.round(rect.height) + '|' + d;
      }
    };
  }

  function round(n) {
    return Math.round(n * 10) / 10;
  }

  /* --------------------------------------------------------------- validation */

  function showError(el, on) {
    var holder = el.closest('.vm-agreement__field, .vm-agreement__signature');
    var error = holder ? holder.querySelector('[data-consent-error]') : null;
    if (error) { error.hidden = !on; }
    if (holder) { holder.classList.toggle('has-error', !!on); }
  }

  function validate(el) {
    var fields = el.querySelectorAll('[data-consent-field][data-required="true"]');
    var ok = true;
    var first = null;

    Array.prototype.forEach.call(fields, function (field) {
      var filled = field.type === 'checkbox' ? field.checked : String(field.value || '').trim() !== '';
      showError(field, !filled);
      if (!filled) {
        ok = false;
        if (!first) { first = field; }
      }
    });

    var canvas = el.querySelector('[data-consent-canvas][data-required="true"]');
    if (canvas && pad) {
      var empty = pad.isEmpty();
      showError(canvas, empty);
      if (empty) {
        ok = false;
        if (!first) { first = canvas; }
      }
    }

    if (first && first.scrollIntoView) {
      first.scrollIntoView({ block: 'center' });
    }

    return ok;
  }

  function collect(el) {
    var props = {};

    Array.prototype.forEach.call(el.querySelectorAll('[data-consent-field]'), function (field) {
      var label = field.getAttribute('data-label') || 'Field';
      var value = field.type === 'checkbox' ? (field.checked ? 'Yes' : 'No') : String(field.value || '').trim();
      if (value === '') { return; }
      // A leading underscore keeps a property on the order but out of the cart,
      // the checkout and anything the customer sees.
      var name = field.getAttribute('data-visible') === 'true' ? label : '_' + label;
      props[name] = value;
    });

    if (pad && !pad.isEmpty()) {
      props._signature = pad.toPath();
    }

    props._signed_at = new Date().toISOString();
    props._agreement_version = el.getAttribute('data-version') || '';

    return props;
  }

  /* -------------------------------------------------------------------- modal */

  var pending = null;

  function open(onDone) {
    var el = root();
    if (!el) { return; }

    pending = onDone || null;
    el.hidden = false;
    document.body.classList.add('vm-agreement-open');

    ensurePad();
    checkScroll(el);

    var focusable = el.querySelector('[data-consent-field], [data-consent-submit]');
    if (focusable && focusable.focus) { focusable.focus(); }
  }

  function close() {
    var el = root();
    if (!el) { return; }
    el.hidden = true;
    pending = null;
    document.body.classList.remove('vm-agreement-open');
  }

  function checkScroll(el) {
    if (el.getAttribute('data-require-scroll') !== 'true') { return; }
    var body = el.querySelector('[data-consent-scroll]');
    var submit = el.querySelector('[data-consent-submit]');
    if (!body || !submit) { return; }

    function update() {
      var atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 8;
      submit.disabled = !atEnd;
      submit.classList.toggle('is-waiting', !atEnd);
    }

    body.removeEventListener('scroll', update);
    body.addEventListener('scroll', update);
    update();
  }

  /* --------------------------------------------------------------------- init */

  /*
   * Everything below is delegated from document rather than bound to elements.
   * The quick add drawer injects the agreement form after page load, so anything
   * that bound on DOMContentLoaded would be dead inside it. The signature pad is
   * built lazily on open, and rebuilt whenever it is a different canvas element,
   * which covers the drawer being opened on one product after another.
   */
  var padCanvas = null;

  function ensurePad() {
    var el = root();
    var canvas = el ? el.querySelector('[data-consent-canvas]') : null;
    if (!canvas) { pad = null; padCanvas = null; return; }
    if (canvas !== padCanvas) {
      pad = createPad(canvas);
      padCanvas = canvas;
    }
    pad.resize();
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) { return; }
    var el = root();
    if (!el) { return; }

    if (event.target.closest('[data-consent-close]')) {
      close();
      return;
    }

    if (event.target.closest('[data-consent-clear]')) {
      if (pad) { pad.clear(); }
      showError(el.querySelector('[data-consent-canvas]'), false);
      return;
    }

    if (event.target.closest('[data-consent-submit]')) {
      if (!validate(el)) { return; }
      collected = collect(el);
      signed = true;
      var done = pending;
      close();
      if (done) { done(); }
    }
  });

  document.addEventListener('keydown', function (event) {
    var el = root();
    if (event.key === 'Escape' && el && !el.hidden) { close(); }
  });

  function init() {
    ensurePad();
  }

  /* ------------------------------------------------------------ add to cart gate */

  // Capture phase on document, so this runs before the delegated jQuery handlers on
  // body and can stop them outright until the agreement is signed.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest('#ProductSubmitButton') : null;
      if (!button) { return; }
      if (!root() || signed) { return; }

      event.preventDefault();
      event.stopImmediatePropagation();

      open(function () {
        // Signed now, so the same click can run through the normal handlers.
        button.click();
      });
    },
    true
  );

  window.vmConsent = {
    isPresent: function () { return !!root(); },
    isSigned: function () { return signed; },
    properties: function () { return collected ? JSON.parse(JSON.stringify(collected)) : null; },
    mergeProperties: function (existing) {
      if (!collected) { return existing; }
      var out = existing || {};
      Object.keys(collected).forEach(function (key) { out[key] = collected[key]; });
      return out;
    },
    open: open,
    close: close,
    reset: function () { signed = false; collected = null; if (pad) { pad.clear(); } }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
